import {
  ConversationDirection,
  LivechatMessageModel,
  MessageKind,
  MessageStructureAttachment,
  MessageStructurePayloadAttachment,
  MessageStructureQuickReply,
} from '@mm/state/model/LivechatMessageModel';
import emojiRegex from 'emoji-regex';
import { safeParseURL } from '../../../../../common/helpers/utils/urlUtils';

const EMOJI_REGEX = emojiRegex();
const REFERRERURL_REF_TOKEN = 'REFERRERURL=';

export type MessageStructureParser =
  | (Pick<LivechatMessageModel, 'structure' | 'campaign'> &
      Partial<Pick<LivechatMessageModel, 'kind' | 'direction'>>)
  | undefined;

const isGalleryAttachment = (
  attachment?: MessageStructureAttachment,
): attachment is MessageStructurePayloadAttachment =>
  attachment?.type === 'template' &&
  attachment.payload?.templateType === 'generic';

export const messageStructureParser = (message: MessageStructureParser) => {
  const getDisplayText = () => {
    const quickReply = message?.structure.quickReply?.payload ?? '';
    return (
      message?.structure.text ||
      message?.structure.attachment?.payload?.text ||
      (message?.kind === MessageKind.postback && message.structure.title) ||
      (message?.kind === MessageKind.quickReply &&
        quickReply.substr(0, quickReply.indexOf('|'))) ||
      (message?.structure?.body &&
        message?.structure?.subject &&
        `Subject: ${message.structure.subject}\n${
          message.structure.previewText
            ? `Preview: ${message.structure.previewText}\n`
            : ''
        }\n${message.structure.body}`) ||
      ''
    );
  };
  const getTextIsEmojiOnly = () =>
    message?.structure.text?.replace(EMOJI_REGEX, '').length === 0;

  const getPayloadUrl = (attachment: MessageStructureAttachment) =>
    (attachment.payload && attachment.payload.url) || undefined;

  const getDisplayItemSrcBase = (type: 'image' | 'file' | 'video' | 'audio') =>
    message?.structure.attachment?.type === type &&
    getPayloadUrl(message.structure.attachment);

  const getDisplayImageSrc = () => getDisplayItemSrcBase('image');

  const getDisplayVideoSrc = () => getDisplayItemSrcBase('video');

  const getDisplayAudioSrc = () => getDisplayItemSrcBase('audio');

  const getAttachmentLink = () => getDisplayItemSrcBase('file');

  const getIsLikeHeartSticker = () =>
    message?.structure.attachment?.type === 'like_heart';

  const getAttachmentLinksWithTypes = () =>
    message?.structure.attachments
      ?.filter(a => !!a.payload && a.type !== 'fallback')
      .map(a => ({
        url: getPayloadUrl(a),
        type: a.type,
      }));

  const getFallbackAttachment = () =>
    (message?.structure?.attachments?.length &&
      message?.structure?.attachments?.[0]?.type === 'fallback' &&
      message.structure.attachments[0]) ||
    undefined;

  const getQuickReplies = () => message?.structure?.quickReplies || [];

  const getQuickReplyContentType = () => {
    const buttons = getQuickReplies();
    const firstQuickReplyButton = buttons[0] as
      | MessageStructureQuickReply
      | undefined;
    if (
      buttons.length === 1 &&
      firstQuickReplyButton &&
      firstQuickReplyButton.contentType
    ) {
      return firstQuickReplyButton.contentType;
    }
    return 'text';
  };

  const getWidgetButtons = () =>
    message?.structure?.attachment?.payload?.buttons || [];

  const getGalleryElements = () =>
    (isGalleryAttachment(message?.structure.attachment) &&
      message?.structure?.attachment?.payload?.elements) ||
    [];

  const getGalleryImageAspectRatio = () =>
    (isGalleryAttachment(message?.structure.attachment) &&
      message?.structure?.attachment?.payload?.imageAspectRatio) ||
    undefined;

  const getIsGallery = () => isGalleryAttachment(message?.structure.attachment);

  const getIsTypingWidget = () =>
    message?.structure.senderAction === 'typing_on';

  const getIsDelayWidget = () => message?.structure.senderAction === 'delay';

  const getTypingWidgetDelay = () =>
    message?.structure.seconds && message.structure.seconds * 1000;

  const getOTNMessage = () =>
    (message?.structure.attachment?.type === 'template' &&
      message.structure.attachment.payload?.templateType ===
        'one_time_notif_req' &&
      message.structure.attachment.payload.title) ||
    undefined;

  const isGetStarted = () =>
    message?.direction === ConversationDirection.incoming &&
    message?.kind === MessageKind.postback &&
    message?.structure?.payload === '<GET_STARTED_PAYLOAD>';

  const isMessengerAd = () =>
    message?.structure?.source === 'ADS' &&
    message?.structure?.type === 'OPEN_THREAD';

  const isSponsoredMessage = () =>
    Boolean(message?.structure?.smAdId) &&
    message?.kind !== MessageKind.postback;

  const isStoryMention = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.structure?.promoterKind === 'instagram_story_mention';

  const isIgInlineReply = () => !!message?.structure?.replyTo?.mid;

  const isIGCommentGuard = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.structure?.promoterKind === 'instagram_comment_guard';

  const isIGWelcomer = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'instagram_welcomer';

  const isCommentGuard = () =>
    message?.kind === MessageKind.referral &&
    message?.structure?.source === 'FB_COMMENT_GUARD';

  const isUserChatMessage = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.kind === MessageKind.message &&
    message?.structure?.tags?.source === 'customer_chat_plugin';

  const isFBCatchAllEngagements = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'facebook_catch_all';

  const isIGCatchAllEngagements = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.structure?.promoterKind === 'instagram_catch_all';

  const isChatWidget = (isFirstMessage: boolean) =>
    (isFirstMessage && message?.structure?.promoterKind === 'customer_chat') ||
    (message?.kind === MessageKind.referral &&
      message?.structure?.source === 'CUSTOMER_CHAT_PLUGIN') ||
    (message?.kind === MessageKind.postback &&
      message?.structure?.payload === '<GET_STARTED_PAYLOAD>' &&
      message?.structure?.referral?.source === 'CUSTOMER_CHAT_PLUGIN') ||
    // Webchat specific payload for interactive chat widgets
    (message?.kind === MessageKind.message &&
      message.structure.referral?.source === 'CUSTOMER_CHAT_PLUGIN');

  const isIceBreaker = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'ice_breaker';

  const isLandingPage = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'landing_page';

  const isQnA = () => message?.structure?.promoterKind === 'trigger';

  const isCheckboxOptin = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'checkbox';

  const isSendToMessengerOptin = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'send_to_messenger';

  const isIGShortLink = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.structure?.promoterKind === 'instagram_link' &&
    (message?.structure.referral?.source === 'SHORTLINK' ||
      message?.structure?.source === 'SHORTLINK');

  const isShortlink = (isFirstMessage: boolean) =>
    isFirstMessage &&
    ((message?.kind === MessageKind.referral &&
      message?.structure?.source === 'SHORTLINK') ||
      (message?.kind === MessageKind.postback &&
        message?.structure?.payload === '<GET_STARTED_PAYLOAD>' &&
        message?.structure?.referral?.source === 'SHORTLINK'));

  const isOTNConsent = () =>
    message?.direction === ConversationDirection.incoming &&
    message?.structure.type === 'one_time_notif_req';

  const isDrip = (isFirstMessage: boolean) =>
    message?.direction === ConversationDirection.outgoing &&
    isFirstMessage &&
    message?.campaign?.purpose === 'drip';

  const isChatBlaster = (isFirstMessage: boolean) =>
    message?.direction === ConversationDirection.outgoing &&
    isFirstMessage &&
    ['none', 'global', 'timezone'].indexOf(message?.campaign?.purpose || '') >=
      0;

  const isAutomation = (isFirstMessage: boolean) =>
    message?.direction === ConversationDirection.outgoing &&
    isFirstMessage &&
    message?.campaign?.purpose === 'automation';

  const isOptinPopup = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'optin';

  const isSMSOptin = (isFirstMessage: boolean) =>
    isFirstMessage && message?.structure?.promoterKind === 'sms_optin';

  const getWebsiteChatUrl = (isFirstMessage: boolean) => {
    if (!message || !isChatWidget(isFirstMessage)) {
      return;
    }
    const urlFromStructure =
      message.structure.url || message.structure.referral?.url;
    if (urlFromStructure) {
      const parsedUrl = safeParseURL(urlFromStructure);
      if (parsedUrl) {
        return parsedUrl;
      }
    }

    if (!message.structure.ref) {
      return;
    }
    const refParts = message.structure.ref.split('_');
    const referrerUrlPart = refParts.find(p =>
      p.startsWith(REFERRERURL_REF_TOKEN),
    );
    if (!referrerUrlPart) {
      return;
    }
    try {
      return safeParseURL(
        atob(referrerUrlPart.substr(REFERRERURL_REF_TOKEN.length)),
      );
    } catch (err) {
      // Ignore errors.
    }
  };

  const getSMSKeyword = () =>
    message?.structure?.promoterKind === 'sms_keyword' &&
    message.structure.promoterName;

  const isDeleted = () => message?.structure?.lcKind === 'deleted';

  const isIGStory = () => message?.kind === MessageKind.igStory;

  const getIsIGCommentGuardMessage = () =>
    message?.kind === MessageKind.igComment;

  const getIsIGLiveCommentMessage = () =>
    message?.kind === MessageKind.liveComment;

  const isMobileMonkeyAppEmbeddedChat = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.kind === MessageKind.referral &&
    message.structure.referral?.source === 'MOBILEMONKEY_APP_EMBEDDED_CHAT';

  const isMobileMonkeyAppEmbeddedChatLLR = (isFirstMessage: boolean) =>
    isFirstMessage &&
    message?.kind === MessageKind.referral &&
    message.structure.referral?.source === 'MOBILEMONKEY_APP_EMBEDDED_CHAT_LLR';

  const getHiddenBodyText = () => message?.structure?.hiddenBody;

  const getEmailAttachmentsNames = () =>
    message?.structure?.emailAttachmentTitles;

  return {
    getDisplayText,
    getTextIsEmojiOnly,
    getQuickReplies,
    getQuickReplyContentType,
    getWidgetButtons,
    getDisplayImageSrc,
    getDisplayVideoSrc,
    getDisplayAudioSrc,
    getAttachmentLink,
    getGalleryElements,
    getGalleryImageAspectRatio,
    getIsGallery,
    getIsTypingWidget,
    getIsDelayWidget,
    getTypingWidgetDelay,
    getAttachmentLinksWithTypes,
    getFallbackAttachment,
    getOTNMessage,
    getWebsiteChatUrl,
    getSMSKeyword,
    getIsLikeHeartSticker,
    isOTNConsent,
    isGetStarted,
    isIceBreaker,
    isIGWelcomer,
    isStoryMention,
    isIGCommentGuard,
    isMessengerAd,
    isSponsoredMessage,
    isChatWidget,
    isUserChatMessage,
    isShortlink,
    isIGShortLink,
    isCommentGuard,
    isDrip,
    isChatBlaster,
    isAutomation,
    isLandingPage,
    isCheckboxOptin,
    isSendToMessengerOptin,
    isOptinPopup,
    isSMSOptin,
    isDeleted,
    isIGStory,
    isIgInlineReply,
    isMobileMonkeyAppEmbeddedChat,
    isMobileMonkeyAppEmbeddedChatLLR,
    isQnA,
    isFBCatchAllEngagements,
    isIGCatchAllEngagements,
    getIsIGCommentGuardMessage,
    getIsIGLiveCommentMessage,
    getHiddenBodyText,
    getEmailAttachmentsNames,
  };
};
